import {
  Alert,
  Anchor,
  Badge,
  Button,
  Code,
  Group,
  MultiSelect,
  Paper,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload, IconPlayerPlay } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type EvalRun, type EvalRunSummary, useAdminQuery } from "../../api/admin";
import { api } from "../../api/client";
import { formatDateTime } from "../../lib/format";

const DEFAULT_CONFIGS = ["fts", "semantic", "hybrid"];

/**
 * Search-quality tools that work without a developer: download the gold set built from the
 * grades, run the evaluation on this server and read the report on screen, keep past reports.
 */
export function EvalTools() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const configs = useAdminQuery<{
    configs: { name: string }[];
    model: string;
    reranker: string | null;
  }>("eval-configs", "/api/admin/eval/configs");
  const gold = useAdminQuery<{ queries: number; relevant: number; judges: string[] }>(
    "eval-gold",
    "/api/admin/eval/gold/summary",
  );
  const reports = useAdminQuery<{ items: { name: string; size: number; modifiedAt: string }[] }>(
    "eval-reports",
    "/api/admin/eval/reports",
  );
  const runs = useAdminQuery<{ items: EvalRunSummary[] }>("eval-runs", "/api/admin/eval/runs");
  const [selected, setSelected] = useState<string[]>(DEFAULT_CONFIGS);
  const [runId, setRunId] = useState<string | null>(null);
  const run = useQuery({
    queryKey: ["admin", "eval-run", runId],
    enabled: runId !== null,
    queryFn: ({ signal }) => api<EvalRun>(`/api/admin/eval/runs/${runId}`, { signal }),
    refetchInterval: (q) => (q.state.data?.status === "running" ? 2000 : false),
  });
  const start = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/api/admin/eval/run", { method: "POST", body: { configs: selected } }),
    onSuccess: (r) => setRunId(r.id),
    onError: (e: Error) => notifications.show({ message: e.message, color: "red" }),
  });
  const status = run.data?.status;
  useEffect(() => {
    if (status === "done" || status === "failed") {
      void queryClient.invalidateQueries({ queryKey: ["admin", "eval-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "eval-reports"] });
    }
  }, [status, queryClient]);
  const result = run.data?.result;
  const lastRuns = runs.data?.items.filter((r) => r.id !== runId).slice(0, 3) ?? [];
  const f = (x: number) => x.toFixed(3);

  return (
    <Paper withBorder p="sm">
      <Stack gap="sm">
        <Text fw={600} size="sm">
          {t("admin.eval.tools")}
        </Text>
        <Text size="xs" c="dimmed">
          {t("admin.eval.toolsHint")}
        </Text>
        <Group gap="sm" align="flex-end" wrap="wrap">
          <Button
            component="a"
            href="/api/admin/eval/gold"
            download
            variant="default"
            size="xs"
            leftSection={<IconDownload size={14} />}
            disabled={!gold.data || gold.data.queries === 0}
          >
            {t("admin.eval.downloadGold")}
          </Button>
          <Text size="xs" c="dimmed">
            {gold.data
              ? t("admin.eval.goldSummary", {
                  queries: gold.data.queries,
                  relevant: gold.data.relevant,
                })
              : ""}
          </Text>
        </Group>
        <Group gap="sm" align="flex-end" wrap="wrap">
          <MultiSelect
            size="xs"
            label={t("admin.eval.configs")}
            data={(configs.data?.configs ?? []).map((c) => c.name)}
            value={selected}
            onChange={setSelected}
            style={{ minWidth: 320 }}
          />
          <Button
            size="xs"
            leftSection={<IconPlayerPlay size={14} />}
            loading={start.isPending || run.data?.status === "running"}
            disabled={selected.length === 0 || !gold.data || gold.data.queries === 0}
            onClick={() => start.mutate()}
          >
            {t("admin.eval.run")}
          </Button>
          {configs.data && (
            <Text size="xs" c="dimmed">
              {configs.data.model}
              {configs.data.reranker ? ` + ${configs.data.reranker}` : ""}
            </Text>
          )}
        </Group>
        {gold.data && gold.data.queries === 0 && (
          <Text size="xs" c="dimmed">
            {t("admin.eval.noGold")}
          </Text>
        )}
        {run.data?.status === "running" && (
          <Code block style={{ maxHeight: 120, overflow: "auto" }}>
            {run.data.log.slice(-6).join("\n") || t("admin.eval.starting")}
          </Code>
        )}
        {run.data?.status === "failed" && <Alert color="red">{run.data.error}</Alert>}
        {result && run.data && (
          <Stack gap="xs">
            <Group gap="sm">
              <Text size="sm" fw={600}>
                {t("admin.eval.result", { queries: run.data.queries })}
              </Text>
              {result.skipped.length > 0 && (
                <Badge variant="light" color="orange">
                  {t("admin.eval.skipped", { count: result.skipped.length })}
                </Badge>
              )}
              {run.data.reportFile && (
                <Anchor size="sm" href={`/api/admin/eval/reports/${run.data.reportFile}`} download>
                  {t("admin.eval.downloadReport")}
                </Anchor>
              )}
            </Group>
            <Table.ScrollContainer minWidth={640}>
              <Table striped withTableBorder verticalSpacing={2}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("admin.eval.config")}</Table.Th>
                    <Table.Th ta="right">recall@5</Table.Th>
                    <Table.Th ta="right">recall@10</Table.Th>
                    <Table.Th ta="right">recall@50</Table.Th>
                    <Table.Th ta="right">MRR@10</Table.Th>
                    <Table.Th ta="right">nDCG@10</Table.Th>
                    <Table.Th ta="right">{t("admin.eval.medianMs")}</Table.Th>
                    <Table.Th ta="right">p95</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {result.results.map((r) => (
                    <Table.Tr key={r.name}>
                      <Table.Td>{r.name}</Table.Td>
                      <Table.Td ta="right">{f(r.recall5)}</Table.Td>
                      <Table.Td ta="right">{f(r.recall10)}</Table.Td>
                      <Table.Td ta="right">{f(r.recall50)}</Table.Td>
                      <Table.Td ta="right">{f(r.mrr10)}</Table.Td>
                      <Table.Td ta="right">{f(r.ndcg10)}</Table.Td>
                      <Table.Td ta="right">{r.medianMs.toFixed(0)}</Table.Td>
                      <Table.Td ta="right">{r.p95Ms.toFixed(0)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Text size="xs" c="dimmed">
              {t("admin.eval.perQuery")}
            </Text>
            <Table.ScrollContainer minWidth={640}>
              <Table verticalSpacing={2} withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("admin.eval.query")}</Table.Th>
                    <Table.Th ta="right">{t("admin.eval.relevant")}</Table.Th>
                    {result.results.map((r) => (
                      <Table.Th key={r.name} ta="right">
                        {r.name}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(result.results[0]?.queries ?? []).map((q, i) => (
                    <Table.Tr key={q.id}>
                      <Table.Td>{q.query}</Table.Td>
                      <Table.Td ta="right">{q.relevantCount}</Table.Td>
                      {result.results.map((r) => {
                        const x = r.queries[i];
                        return (
                          <Table.Td
                            key={r.name}
                            ta="right"
                            {...(x && x.recall10 === 0 ? { c: "red" } : {})}
                          >
                            {x ? `${x.recall10.toFixed(2)} / ${x.firstRank ?? "—"}` : ""}
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        )}
        {lastRuns.length > 0 && (
          <Stack gap={2}>
            <Text size="xs" fw={600}>
              {t("admin.eval.lastRuns")}
            </Text>
            {lastRuns.map((r) => (
              <Group key={r.id} gap="xs">
                <Text size="xs">{formatDateTime(r.startedAt)}</Text>
                <Badge
                  size="xs"
                  variant="light"
                  color={r.status === "done" ? "green" : r.status === "failed" ? "red" : "gray"}
                >
                  {r.status}
                </Badge>
                <Text size="xs" c="dimmed">
                  {r.summary
                    ?.map((c) => `${c.name} recall@10=${c.recall10.toFixed(2)}`)
                    .join(" · ") ??
                    r.error ??
                    ""}
                </Text>
              </Group>
            ))}
          </Stack>
        )}
        {(reports.data?.items.length ?? 0) > 0 && (
          <Stack gap={2}>
            <Text size="xs" fw={600}>
              {t("admin.eval.reports")}
            </Text>
            {reports.data?.items.slice(0, 10).map((r) => (
              <Group key={r.name} gap="xs">
                <Anchor size="xs" href={`/api/admin/eval/reports/${r.name}`} download>
                  {r.name}
                </Anchor>
                <Text size="xs" c="dimmed">
                  {formatDateTime(r.modifiedAt)}
                </Text>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
